import React, { useEffect, useRef, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  ComponentDTO,
  InvoiceDTO,
  InvoiceLineItemDTO,
  InvoiceTypeDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import Drawer from '../common/Drawer';
import InvoiceLineItemTable from '../tables/InvoiceLineItemTable';
import AddButton from '../common/AddButton';
import { confirmDelete } from '../../utils/sweetAlertUtils';
import Button from '../common/Button';
import { useInvoiceTypeApi } from '../../hooks/useInvoiceTypeApi';
import { useComponents } from '../../hooks/useComponentApi';
import { useMemberApi } from '../../hooks/useMemberApi';
import { useVatRateHistorySearch } from '../../hooks/useVatRateHistoryApi';
import ErrorField from '../common/formElements/ErrorField';
import SimpleAlert from '../common/SimpleAlert';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import { InvoiceTypeConstants } from '../../constants/InvoiceTypeConstants';

const SALES_ORDER_TYPE_IDS: string[] = [
  InvoiceTypeConstants.SALES_ORDER_ONCE_OFF,
  InvoiceTypeConstants.SALES_ORDER_RENEWAL,
];

interface InvoiceLineItem extends InvoiceLineItemDTO {
  arrayIndex?: number;
}

interface InvoiceFormProps {
  initialValues: InvoiceDTO;
  onSubmit: (data: InvoiceDTO) => void;
  onSubmitFinalized?: (data: InvoiceDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
  showMemberPaidUpToField?: boolean;
  memberPaidUpTo?: string;
  currentMemberPaidUpTo?: Date;
  onMemberPaidUpToChange?: (value: string) => void;
  shouldFinalize?: boolean;
}

interface PaymentStatus {
  message: string;
  difference: number;
  alertType: 'error' | 'success' | 'warning' | 'info' | undefined;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  lineItem: {
    id: 'lineItems',
    title: 'Line Items',
  },
} as const;

const defaultLineItem: InvoiceLineItemDTO = {};

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  initialValues,
  onSubmit,
  onSubmitFinalized,
  onCancel,
  isLoading = false,
  showMemberPaidUpToField = false,
  memberPaidUpTo = '',
  currentMemberPaidUpTo,
  onMemberPaidUpToChange,
  shouldFinalize = false,
}) => {
  const [formData, setFormData] = useState<InvoiceDTO>(initialValues);
  const didInitializePrefilledTotalsRef = useRef(false);
  const [lineItem, setLineItem] = useState<InvoiceLineItem>(defaultLineItem);
  const [selectedDate, setSelectedDate] = useState<string>();
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceTypeDTO[]>([]);
  const [components, setComponents] = useState<ComponentDTO[]>([]);
  const [vatRate, setVatRate] = useState<number>(15); // Default 15%, will be fetched from CompanyInfo

  const [isLineItemDrawerOpen, setIsLineItemDrawerOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>({
    message: '',
    difference: 0,
    alertType: undefined,
  });
  const [memberOptions, setMemberOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [originalMember, setOriginalMember] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { search: searchMembers, isLoading: isLoadingMembers } = useMemberApi();
  const { search: searchInvoiceTypes, isLoading: isLoadingInvoiceTypes } = useInvoiceTypeApi();
  const { data: componentsData, isLoading: isLoadingComponents } = useComponents();
  const { search: searchVatRateHistories } = useVatRateHistorySearch();

  const isSalesOrder = SALES_ORDER_TYPE_IDS.includes(formData.invoiceTypeId || '');

  const validationRules: ValidationRules = {
    memberId: [{ required: true, message: 'Member is required' }],
    invoiceTypeId: [{ required: true, message: 'Type is required' }],
    adminComponentId: [{ required: true, message: 'Component is required' }],
    yearMonth: [{ required: true, message: 'Year Month is required' }],
    paidDate: isSalesOrder
      ? []
      : [
        { required: true, message: 'Paid Date is required' },
        {
          custom: (value) => !isNaN(Date.parse(value)),
          message: 'Paid Date must be a valid date',
        },
      ],
    totalPaid: [
      { required: true, message: 'Total Paid is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Total Paid must be a positive number',
      },
    ],
    invoiceLineItems: [
      { required: true, message: 'At least one line item is required' },
    ],
  };

  const lineItemValidationRules: ValidationRules = {
    description: [{ required: true, message: 'Total Paid is required' }],
    priceIncl: [
      { required: true, message: 'Amount is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Amount must be a positive number',
      },
    ],
    quantity: [
      { required: true, message: 'Quantity is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Quantity must be a positive number',
      },
    ],
  };

  const { errors, validate } = useFormValidation<InvoiceDTO>(validationRules);
  const { errors: lineItemErrors, validate: lineItemValidate } =
    useFormValidation<InvoiceDTO>(lineItemValidationRules);

  const fetchInvoiceTypes = async () => {
    try {
      const response = await searchInvoiceTypes({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setInvoiceTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching invoice types:', error);
    }
  };

  const fetchCurrentVatRate = async () => {
    try {
      const response = await searchVatRateHistories({
        filters: {},
        page: 1,
        pageSize: 1,
        sortBy: 'EffectiveDate',
        sortDescending: true,
      });
      const currentRate = response?.results?.[0]?.rate;
      if (currentRate) {
        setVatRate(currentRate);
        console.log(`VAT rate loaded from VatRateHistory: ${currentRate}%`);
      }
    } catch (error) {
      console.error('Error fetching VAT rate history, using default 15% VAT:', error);
    }
  };

  useEffect(() => {
    fetchInvoiceTypes();
    fetchCurrentVatRate();
  }, []);

  useEffect(() => {
    setComponents(componentsData || []);
  }, [componentsData]);

  // If the invoice was prefilled with line items (e.g. from a Recon Transaction),
  // assign array indexes and calculate totals.
  useEffect(() => {
    if (didInitializePrefilledTotalsRef.current) return;
    if (!initialValues.invoiceLineItems || initialValues.invoiceLineItems.length === 0) return;

    setFormData((prev) => {
      const existingLineItems = (prev.invoiceLineItems as InvoiceLineItem[]) || [];

      // Assign arrayIndex and ensure line item totals are calculated
      const itemsWithIndexAndTotals = existingLineItems.map((item, index) => {
        let updatedItem = item;
        
        // Assign arrayIndex if needed
        if (!item.id && item.arrayIndex === undefined) {
          updatedItem = { ...item, arrayIndex: index + 1 };
        }
        
        // Calculate line item totals if they're missing (backend might have provided them)
        if (!item.lineIncl || !item.lineExcl || !item.lineTax) {
          updatedItem = calculateLineItemTotals(updatedItem);
        }
        
        return updatedItem;
      });

      const totals = calculateInvoiceTotals(itemsWithIndexAndTotals);
      return {
        ...prev,
        invoiceLineItems: itemsWithIndexAndTotals,
        ...totals,
      };
    });

    didInitializePrefilledTotalsRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.invoiceLineItems]);

  useEffect(() => {
    const populateInitialMember = async () => {
      if (initialValues.memberId && initialValues.member) {
        const memberOption = {
          value: initialValues.memberId,
          label: `${initialValues.member.firstName || ''} ${initialValues.member.lastName || ''} (${initialValues.member.idPassportNumber || ''})`,
        };
        setMemberOptions([memberOption]);
        setOriginalMember(memberOption);
      }
    };

    populateInitialMember();
  }, [initialValues.memberId, initialValues.member]);

  const handleMemberSearch = async (query: string) => {
    const response = await searchMembers({
      filters: {
        generalSearchTerm: query,
      },
      page: 1,
      pageSize: 20,
      sortBy: 'firstName',
    });

    const searchResults =
      response?.data?.results?.map((member: any) => ({
        value: member.id ?? '',
        label: `${member.firstName ?? ''} ${member.lastName ?? ''} (${member.idPassportNumber ?? ''})`,
      })) || [];

    const optionsToShow = originalMember
      ? [
        originalMember,
        ...searchResults.filter(
          (option) => option.value !== originalMember.value,
        ),
      ]
      : searchResults;

    setMemberOptions(optionsToShow);
  };

  const handleMemberChange = (
    e: React.ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >,
  ) => {
    const selectedValue = e.target.value;

    if (!selectedValue && originalMember) {
      setFormData((prev) => ({
        ...prev,
        memberId: originalMember.value,
      }));
      setMemberOptions([originalMember]);
    } else {
      setFormData((prev) => ({
        ...prev,
        memberId: selectedValue,
      }));
    }
  };

  // Calculate line item totals (lineIncl, lineExcl, lineTax) from priceIncl and quantity
  // NOTE: priceIncl is INCLUSIVE of VAT, so we REMOVE VAT to get the exclusive amounts
  const calculateLineItemTotals = (lineItemData: InvoiceLineItem): InvoiceLineItem => {
    const priceIncl = Number(lineItemData.priceIncl) || 0;
    const quantity = Number(lineItemData.quantity) || 0;
    const lineDiscount = Number(lineItemData.lineDiscount) || 0;
    
    // Calculate VAT multiplier from percentage (e.g., 15% → 1.15)
    const vatMultiplier = 1 + (vatRate / 100);
    
    // Remove VAT from inclusive price (matches backend VatCalculator.RemoveVat)
    const priceExcl = Math.round((priceIncl / vatMultiplier) * 100) / 100;
    const priceTax = priceIncl - priceExcl;
    
    // Calculate line totals
    const lineExcl = priceExcl * quantity;
    const lineTax = priceTax * quantity;
    const lineIncl = lineExcl + lineTax - lineDiscount;
    
    return {
      ...lineItemData,
      priceExcl: Number(priceExcl.toFixed(2)),
      priceTax: Number(priceTax.toFixed(2)),
      lineIncl: Number(lineIncl.toFixed(2)),
      lineExcl: Number(lineExcl.toFixed(2)),
      lineTax: Number(lineTax.toFixed(2)),
      lineDiscount: lineDiscount,
    };
  };

  const calculateInvoiceTotals = (lineItems: InvoiceLineItem[]) => {
    // Sum the totals from the line items
    const totalIncl = lineItems.reduce((sum, item) => {
      return sum + (Number(item.lineIncl) || 0);
    }, 0);

    const totalExcl = lineItems.reduce((sum, item) => {
      return sum + (Number(item.lineExcl) || 0);
    }, 0);

    const totalTax = lineItems.reduce((sum, item) => {
      return sum + (Number(item.lineTax) || 0);
    }, 0);

    return {
      totalIncl: Number(totalIncl.toFixed(2)),
      totalExcl: Number(totalExcl.toFixed(2)),
      totalTax: Number(totalTax.toFixed(2)),
      totalDiscount: 0,
    };
  };

  const checkTotalPaid = (): PaymentStatus => {
    const totalPaid = Number(formData.totalPaid) || 0;
    const totalIncl = Number(formData.totalIncl) || 0;
    const difference = totalPaid - totalIncl;

    if (difference > 0) {
      return {
        message: `WARNING client paid R ${Math.abs(difference).toFixed(2)} too much. Total Price is R ${totalIncl.toFixed(2)}`,
        difference: difference,
        alertType: 'warning',
      };
    } else if (difference < 0) {
      return {
        message: `WARNING client paid R ${Math.abs(difference).toFixed(2)} too little. Total Price is R ${totalIncl.toFixed(2)}`,
        difference: difference,
        alertType: 'error',
      };
    } else {
      return {
        message: `Full amount to be paid is R ${totalIncl.toFixed(2)}`,
        difference: 0,
        alertType: 'success',
      };
    }
  };

  useEffect(() => {
    setPaymentStatus(checkTotalPaid());
  }, [formData.totalPaid, formData.totalIncl]);

  // Sales orders are unpaid by definition - clear the paid fields when this type is selected
  useEffect(() => {
    if (isSalesOrder && (formData.totalPaid !== 0 || formData.paidDate)) {
      setFormData((prev) => ({
        ...prev,
        totalPaid: 0,
        paidDate: '',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalesOrder]);

  const handleSave = () => {
    if (validate(formData)) {
      if (shouldFinalize && onSubmitFinalized) {
        onSubmitFinalized(formData);
      } else {
        onSubmit(formData);
      }
    }
  };

  const convertYearMonthToDate = (yearMonth: string): string => {
    if (!yearMonth || yearMonth.length !== 6) return '';

    const year = yearMonth.substring(0, 4);
    const month = yearMonth.substring(4, 6);

    return `${year}-${month}-01`;
  };

  const handleYearMonthChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const selectedDate = e.target.value;
    setSelectedDate(selectedDate);

    if (selectedDate) {
      const date = new Date(selectedDate);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const yearMonthString = `${year}${month.toString().padStart(2, '0')}`;
      console.log(yearMonthString);

      setFormData((prev) => ({
        ...prev,
        yearMonth: yearMonthString,
        year: year,
        month: month,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        yearMonth: '',
        year: undefined,
        month: undefined,
      }));
    }
  };

  const handleLineItemSave = () => {
    if (lineItem && lineItemValidate(lineItem)) {
      setFormData((prev) => {
        const existingLineItems =
          (prev.invoiceLineItems as InvoiceLineItem[]) || [];

        let existingIndex = -1;

        if (lineItem.id) {
          existingIndex = existingLineItems.findIndex(
            (item) => item.id === lineItem.id,
          );
        } else if (lineItem.arrayIndex !== undefined) {
          existingIndex = existingLineItems.findIndex(
            (item) => item.arrayIndex === lineItem.arrayIndex,
          );
        }

        // Calculate line item totals (lineIncl, lineExcl, lineTax)
        const calculatedLineItem = calculateLineItemTotals(lineItem);

        let updatedLineItems: InvoiceLineItem[];

        if (existingIndex !== -1) {
          // Editing existing item
          console.log('Editing: ', calculatedLineItem);
          updatedLineItems = existingLineItems.map((item, index) => {
            if (index === existingIndex) {
              return calculatedLineItem;
            }
            return item;
          });
        } else {
          // Adding new item - assign arrayIndex
          console.log('Adding: ', calculatedLineItem);
          const newArrayIndex =
            Math.max(
              ...existingLineItems.map((opt) => opt.arrayIndex || 0),
              0,
            ) + 1;
          const newLineItem = {
            ...calculatedLineItem,
            arrayIndex: newArrayIndex,
          };
          updatedLineItems = [...existingLineItems, newLineItem];
        }

        const calculatedTotals = calculateInvoiceTotals(updatedLineItems);
        const isSalesOrderType = SALES_ORDER_TYPE_IDS.includes(prev.invoiceTypeId || '');

        return {
          ...prev,
          invoiceLineItems: updatedLineItems,
          ...calculatedTotals,
          // Sales orders are unpaid by definition - never auto-fill totalPaid for them
          totalPaid: isSalesOrderType ? 0 : calculatedTotals.totalIncl,
        };
      });

      handleDrawerClose();
    }
  };

  const handleLineItemEdit = (optionToEdit: InvoiceLineItemDTO) => {
    setLineItem(optionToEdit);
    setIsLineItemDrawerOpen(true);
  };

  const handleLineItemDelete = async (lineItemToDelete: InvoiceLineItem) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setFormData((prev) => {
      const updatedLineItems =
        (prev.invoiceLineItems as InvoiceLineItem[])?.filter((lineItem) => {
          if (lineItemToDelete.id && lineItem.id) {
            return lineItem.id !== lineItemToDelete.id;
          }
          if (lineItemToDelete.arrayIndex && lineItem.arrayIndex) {
            return lineItem.arrayIndex !== lineItemToDelete.arrayIndex;
          }
          return lineItem !== lineItemToDelete;
        }) || [];

      const calculatedTotals = calculateInvoiceTotals(updatedLineItems);
      const isSalesOrderType = SALES_ORDER_TYPE_IDS.includes(prev.invoiceTypeId || '');

      return {
        ...prev,
        invoiceLineItems: updatedLineItems,
        ...calculatedTotals,
        // Sales orders are unpaid by definition - never auto-fill totalPaid for them
        totalPaid: isSalesOrderType ? 0 : calculatedTotals.totalIncl,
      };
    });
  };

  const handleDrawerClose = () => {
    setLineItem(defaultLineItem);
    setIsLineItemDrawerOpen(false);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
            <FormField
              id="memberSearch"
              label="Search Member"
              type="dropdown"
              value={formData.memberId || ''}
              onChange={(e) => handleMemberChange(e)}
              options={memberOptions}
              placeholder="Type to search members..."
              required={true}
              error={errors.memberId}
              helper="Only active Member will display in this list"
              isLoading={isLoadingMembers}
              searchable
              onSearch={handleMemberSearch}
              searchDebounceMs={300}
            />

          <FormField
            id="invoiceTypeId"
            label="Type"
            type="dropdown"
            value={formData.invoiceTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Type"
            required={true}
            error={errors.invoiceTypeId}
            options={invoiceTypes.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingInvoiceTypes}
          />

          <FormField
            id="adminComponentId"
            label="Component"
            type="dropdown"
            value={formData.adminComponentId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Component"
            required={true}
            error={errors.adminComponentId}
            options={components.map((type) => ({
              value: type.id,
              label: type.title || '',
            }))}
            isLoading={isLoadingComponents}
          />

          <FormField
            id="yearMonth"
            label="Invoiced for Year/Month"
            type="date"
            value={selectedDate || convertYearMonthToDate(formData.yearMonth || '')}
            onChange={(e) => handleYearMonthChange(e)}
            placeholder="Select Date"
            required={true}
            error={errors.yearMonth}
          />

          {!isSalesOrder && (
            <FormField
              id="paidDate"
              label="Date Paid"
              type="date"
              value={formData.paidDate || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Select Date"
              required={true}
              error={errors.paidDate}
            />
          )}

          <FormField
            id="totalPaid"
            label="Total Paid"
            type="number"
            value={isSalesOrder ? 0 : formData.totalPaid || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Total Paid"
            required={true}
            error={errors.totalPaid}
            disabled={isSalesOrder}
            helper={isSalesOrder ? 'Sales orders are unpaid — amount is fixed at 0' : undefined}
          />

          {showMemberPaidUpToField && (
            <FormField
              id="memberPaidUpTo"
              label={`New Paid-up Date${currentMemberPaidUpTo ? ` (Current: ${formatDateForInput(currentMemberPaidUpTo)})` : ''}`}
              type="date"
              value={memberPaidUpTo}
              onChange={(e) => onMemberPaidUpToChange?.(e.target.value)}
              placeholder="Select Date"
              required={false}
            />
          )}
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.lineItem.id}
        title={FORM_SECTIONS.lineItem.title}
        headerActions={
          <AddButton
            onClick={() => {
              setLineItem(defaultLineItem);
              setIsLineItemDrawerOpen(true);
            }}
            variant="secondary"
            label="Add Line Item"
          />
        }
      >
        <InvoiceLineItemTable
          data={formData.invoiceLineItems || []}
          handleEdit={(dto) => handleLineItemEdit(dto)}
          handleDelete={(dto) => handleLineItemDelete(dto)}
          currentPage={currentPage}
          totalPages={Math.ceil(
            (formData.invoiceLineItems?.length || 0) / pageSize,
          )}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={false}
        />
        <ErrorField error={errors.invoiceLineItems} />

        {/* Professional invoice summary card */}
        <div className="mt-8 bg-gray-50 rounded-lg border border-gray-200 p-6">
          <div className="space-y-3">
            <div className="flex justify-between items-center text-gray-700">
              <span className="text-sm font-medium">Subtotal (Excl VAT)</span>
              <span className="text-lg font-semibold">
                R {formData.totalExcl?.toFixed(2) || '0.00'}
              </span>
            </div>

            <div className="flex justify-between items-center text-gray-700">
              <span className="text-sm font-medium">
                VAT
              </span>
              <span className="text-lg font-semibold">
                R {formData.totalTax?.toFixed(2) || '0.00'}
              </span>
            </div>

            <div className="border-t border-gray-300 pt-3 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-base font-bold text-gray-900">
                  Total (Incl VAT)
                </span>
                <span className="text-2xl font-bold text-primary">
                  R {formData.totalIncl?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment status alert */}
        {paymentStatus.alertType && (
          <SimpleAlert
            variant={paymentStatus.alertType}
            message={paymentStatus.message}
          />
        )}
      </FormSection>

      <Drawer
        isOpen={isLineItemDrawerOpen}
        onClose={handleDrawerClose}
        title={lineItem.id || lineItem.arrayIndex !== undefined ? 'Edit Line Item' : 'Add Line Item'}
      >
        <div className="space-y-4">
          <FormField
            id="description"
            label="Description"
            type="text"
            value={lineItem.description || ''}
            onChange={(e) => handleChange(e, setLineItem)}
            placeholder="Enter Description"
            required={true}
            error={lineItemErrors.description}
          />
          <FormField
            id="priceIncl"
            label="Amount"
            type="number"
            value={lineItem.priceIncl || ''}
            onChange={(e) => handleChange(e, setLineItem)}
            placeholder="Enter Price Incl"
            required={true}
            error={lineItemErrors.priceIncl}
          />
          <FormField
            id="quantity"
            label="Quantity"
            type="number"
            value={lineItem.quantity || ''}
            onChange={(e) => handleChange(e, setLineItem)}
            placeholder="Enter Quantity"
            required={true}
            error={lineItemErrors.quantity}
          />
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={handleDrawerClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleLineItemSave}>
              {lineItem.id || lineItem.arrayIndex !== undefined ? 'Save Changes' : 'Add Line Item'}
            </Button>
          </div>
        </div>
      </Drawer>
    </Form>
  );
};

export default InvoiceForm;
